import React, { useCallback } from 'react';

import classnames from 'classnames';

import { TertiaryButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import { useShowPageAccessPortalForCasting } from 'components/Globals/PortalsProvider';

import {
  BOOLEAN_OPERATORS,
  BOOLEAN_SEARCH_PARTS,
  CASTING_TOOL_SLUGS,
  EVENTS,
  TP,
  URL_SLUG_TYPES,
} from 'constants/index';

import { getBooleanExpressionString, getBooleanSearchQuery } from 'utils/casting';
import { dispatchEvent } from 'utils/event';
import useAppContext from 'utils/hooks/useAppContext';
import usePageContext from 'utils/hooks/usePageContext';

import { useTranslation } from 'src/i18n';

import {
  CASTING_RESULT_TYPES_VALUES,
  ENTITY_TYPES,
  FILTER_TYPES,
  ROUTE_RESERVED_KEYWORDS,
} from 'operabase-router/constants';

import classes from './RoleOverlapBtn.module.scss';

const RoleOverlapBtn = ({ role, className, trackingData = {}, vertical = false, sideBar = false, orange = false }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { navigate, permissions } = usePageContext();
  const { applyFilters } = usePageContext();
  const { obRouteContext } = useAppContext();
  const showPageAccessPortalForCasting = useShowPageAccessPortalForCasting();
  const { castingToolPermissions, isAdmin } = permissions;
  const hasCastingToolAccess = isAdmin || castingToolPermissions?.hasAccess;
  const { entity } = obRouteContext?.page || {};
  const { filters, tab } = obRouteContext?.linkProps || {};
  const isRoleDebut = tab === CASTING_RESULT_TYPES_VALUES.ROLE_DEBUT;
  const composerName = role?.work?.creators?.[0]?.short_name || role?.work?.creators?.[0]?.name;

  const handleRoleOverlapClick = useCallback(() => {
    if (!hasCastingToolAccess) {
      showPageAccessPortalForCasting();
      return;
    }

    if (!role.work?.id) {
      const linkProps = navigate.getLinkProps({
        baseRoute: ROUTE_RESERVED_KEYWORDS.artists,
        filters: {
          [FILTER_TYPES.PROFESSION]: [
            {
              entity: role,
              entityType: ENTITY_TYPES.PROFESSION,
            },
          ],
        },
      });
      window.open(linkProps.url, '_blank');
      return;
    }

    dispatchEvent(EVENTS.ADD_ANOTHER_ROLE);
    const currentQuery = getBooleanSearchQuery(filters?.expression) || [];
    const newInput = {
      type: BOOLEAN_SEARCH_PARTS.QUERY,
      value: {
        ...(role?.work?.id && { [URL_SLUG_TYPES.WORK]: role.work.id }),
        ...(role?.work?.id && { [URL_SLUG_TYPES.WORK]: role.work.id }),
        ...(role?.id && {
          [URL_SLUG_TYPES.WORK_ROLE]: { id: role.id, type: role.type || CASTING_TOOL_SLUGS.WORK_ROLE },
        }),
      },
    };

    const booleanQuery = [
      ...currentQuery,
      { type: BOOLEAN_SEARCH_PARTS.OPERATOR, value: BOOLEAN_OPERATORS.AND },
      newInput,
    ];
    const updatedQuery = currentQuery.length > 0 ? booleanQuery : [newInput];
    const newExpression = getBooleanExpressionString(updatedQuery, isRoleDebut);

    if (entity?.id && role?.work?.id) {
      const linkProps = navigate.getLinkProps({
        baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
        filters: {
          [FILTER_TYPES.BOOLEAN_SEARCH]: newExpression,
        },
        tab: CASTING_RESULT_TYPES_VALUES.PERFORMED,
      });
      window.open(linkProps.url, '_blank');
    }

    if (role?.work?.id && !entity?.id) {
      applyFilters({
        baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
        filters: {
          ...filters,
          [FILTER_TYPES.BOOLEAN_SEARCH]: newExpression,
        },
        tab: tab || CASTING_RESULT_TYPES_VALUES.PERFORMED,
      });
    }
  }, [
    role,
    filters,
    isRoleDebut,
    entity?.id,
    navigate,
    tab,
    applyFilters,
    hasCastingToolAccess,
    showPageAccessPortalForCasting,
  ]);

  return (
    <TertiaryButton
      styles={{
        root: classnames(classes.roleCard, {
          [className]: !!className,
          [classes.roleCard_vertical]: vertical,
          [classes.roleCard_orange]: orange,
        }),
      }}
      rightIcon={<SpriteIcon icon={entity?.id ? 'search' : 'add_circle'} size={16} />}
      onClick={handleRoleOverlapClick}
      trackingData={{
        ...trackingData,
        subComponent: SUB_COMPONENTS.ROLE_OVERLAP_ITEM,
      }}
    >
      <div className={classes.roleCard_info}>
        <div className={classnames(classes.roleName, { [classes.roleName_sideBar]: sideBar })}>
          <SpriteIcon icon="comedy_mask" size={12} />
          <Typography weight="medium" size={12} className={classes.title}>
            {role.name}
          </Typography>
          {role?.overlapPercentage && (
            <Typography size={12} className={classes.overlap}>
              ({role?.overlapPercentage.toFixed(2)} % {t(`${TP}.OVERLAP`)})
            </Typography>
          )}
          {role?.count && (
            <Typography size={11} className={classes.overlap} color="quinary">
              ({role?.count} {t(`${TP}.PROFILES`)})
            </Typography>
          )}
        </div>
        {role?.work && (
          <div className={classes.work}>
            <Typography size={12} className={classes.workName}>
              {role?.work?.name},{' '}
            </Typography>
            {composerName && (
              <Typography size={12} className={classes.composerName} color="secondary" italic>
                {composerName}
              </Typography>
            )}
          </div>
        )}
      </div>
    </TertiaryButton>
  );
};

export default RoleOverlapBtn;
